import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { EventApi, EventContentArg, EventInput } from "@fullcalendar/core";
import moment from "moment";
import "./calendar-custom.css";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Chip,
  Autocomplete,
  SelectChangeEvent,
} from "@mui/material";
import {
  addEventToGroupService,
  deleteEventGroupService,
  getEventGroupService,
  updateEventGroupService,
} from "../../../services/event-service";
import {
  getGroupByIdService,
  getUserGroupService,
} from "../../../services/group-service";
import { ArrowLeft, ArrowRight, Add, Today, CalendarMonth } from "@mui/icons-material";
import { RootState } from "../../../redux/store/store";
import { useSelector } from "react-redux";

type GroupOption = { label: string; value: number };
type SubjectOption = { id: number; label: string };

type CalendarEvent = EventInput & {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  discordUrl?: string;
  classId: number;
  classTitle: string;
  subjectId?: number | null;
  subjectTitle?: string;
  subjects?: string[];
};

type EventFormState = {
  id: string | null;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  classId: number | null;
  subjectId: number | null;
  discordUrl: string;
};

const colorOptions = [
  { hex: "#0369A1", rgba: "rgba(3, 105, 161, 0.3)" },
  { hex: "#FEBA0C", rgba: "rgba(254, 186, 12, 0.3)" },
  { hex: "#00DF76", rgba: "rgba(0, 223, 118, 0.3)" },
  { hex: "#9C4DF4", rgba: "rgba(156, 77, 244, 0.3)" },
  { hex: "#F8BAD4", rgba: "rgba(248, 186, 212, 0.3)" },
  { hex: "#CC6144", rgba: "rgba(204, 97, 68, 0.3)" },
];

// Event List Item Component
const EventListItem: React.FC<{ event: CalendarEvent; onClick: () => void }> = ({ event, onClick }) => {
  const startTime = moment(event.start).format("HH:mm");
  const endTime = moment(event.end).format("HH:mm");
  const eventDate = moment(event.start).format("ddd, D MMM");
  const isToday = moment(event.start).isSame(moment(), 'day');
  const isPast = moment(event.start).isBefore(moment(), 'day');

  return (
    <div
      onClick={onClick}
      className="group relative p-4 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl border-2 border-white/60 hover:border-white/90"
      style={{
        background: `linear-gradient(135deg, ${event.backgroundColor?.replace(/0\.3\)/, '0.15)') || "rgba(241,245,249,0.4)"} 0%, ${event.backgroundColor?.replace(/0\.3\)/, '0.08)') || "rgba(241,245,249,0.2)"} 100%)`,
      }}
    >
      {/* Status Indicator */}
      <div className="absolute top-0 left-0 w-1.5 h-full rounded-l-2xl" style={{
        background: event.backgroundColor?.replace(/0\.3\)/, '0.9)') || "#0369A1",
      }} />

      {/* Date Badge */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${isToday ? 'bg-gradient-to-r from-[#10b981] to-[#059669] text-white' : isPast ? 'bg-slate-200 text-slate-600' : 'bg-white text-slate-700'} shadow-md`}>
            {eventDate}
          </div>
          {isToday && (
            <span className="px-2 py-1 bg-[#10b981]/20 text-[#059669] text-xs font-bold rounded-lg">Aujourd'hui</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/90 rounded-lg shadow-sm">
          <svg className="w-3.5 h-3.5 text-[#10b981]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-bold text-slate-700">{startTime} - {endTime}</span>
        </div>
      </div>

      {/* Event Title */}
      <h3 className="text-base font-extrabold text-slate-900 mb-2 group-hover:text-[#09745F] transition-colors" style={{
        textShadow: "0 1px 2px rgba(255,255,255,0.8)",
      }}>
        {event.title}
      </h3>

      {/* Class and Subject Info */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/90 rounded-lg shadow-sm">
          <svg className="w-3.5 h-3.5 text-[#09745F]" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
          </svg>
          <span className="text-xs font-semibold text-slate-700">{event.classTitle}</span>
        </div>
        {event.subjectTitle && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#09745F]/10 to-[#00B69B]/10 rounded-lg">
            <span className="text-xs font-semibold text-[#09745F]">{event.subjectTitle}</span>
          </div>
        )}
      </div>

      {/* Hover Arrow */}
      <div className="absolute transition-all duration-300 -translate-y-1/2 opacity-0 right-3 top-1/2 group-hover:opacity-100 group-hover:translate-x-1">
        <svg className="w-5 h-5 text-[#09745F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

// Compact Event Renderer for Mini Calendar
function renderCompactEventContent(eventInfo: EventContentArg) {
  return (
    <div
      className="px-1.5 py-0.5 text-[10px] font-bold truncate rounded"
      style={{
        backgroundColor: eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.6)') || "rgba(3, 105, 161, 0.6)",
        color: 'white',
      }}
    >
      {eventInfo.event.title}
    </div>
  );
}

const Calendar: React.FC = () => {
  const calendarRef = useRef<FullCalendar>(null);
  const [currentDate, setCurrentDate] = useState(moment().format("MMMM, YYYY"));
  const [activeView, setActiveView] = useState("dayGridMonth");
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const [subjectOptionsByGroup, setSubjectOptionsByGroup] = useState<
    Record<number, SubjectOption[]>
  >({});
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState<CalendarEvent | null>(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const userRole = useSelector(
    (state: RootState) => state?.user?.userData?.role.name,
  );
  const canManageEvents = userRole === "ROLE_ADMIN" || userRole === "ROLE_SUPER_TEACHER";

  const [eventData, setEventData] = useState<EventFormState>({
    id: null,
    title: "",
    start: "",
    end: "",
    backgroundColor: colorOptions[0].rgba,
    classId: null,
    subjectId: null,
    discordUrl: "",
  });

  // Fetch groups + hydrate all events the user can see (students only see their groups)
  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const res = await getUserGroupService();
        const options: GroupOption[] =
          res?.data?.map((group: any) => ({
            label: group.title,
            value: group.id,
          })) ?? [];

        setGroupOptions(options);
        setSelectedGroups(options);

        const defaultClassId = options[0]?.value ?? null;
        setEventData((prev) => ({ ...prev, classId: defaultClassId }));

        await loadGroupEvents(options);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  const loadGroupEvents = async (groupsToLoad: GroupOption[]) => {
    if (!groupsToLoad.length) {
      setEvents([]);
      return;
    }

    const unwrap = (res: any) => res?.data?.data ?? res?.data ?? res;

    const results = await Promise.all(
      groupsToLoad.map(async (group) => {
        try {
          const [eventsRes, groupRes] = await Promise.all([
            getEventGroupService(group.value),
            getGroupByIdService(group.value),
          ]);

          const groupData = unwrap(groupRes);
          const subjects: SubjectOption[] =
            groupData?.subjects
              ?.map((s: any) => {
                const id = s?.id ?? null;
                if (id === null || id === undefined) return null;
                const subjectName =
                  s?.speciality ??
                  s?.title ??
                  s?.name ??
                  `Matiere ${id}`;
                const label = `${subjectName} (${s?.level ?? 'N/A'})`;
                return { id: Number(id), label };
              })
              ?.filter((s: SubjectOption | null): s is SubjectOption => Boolean(s)) ?? [];

          const eventDataList = unwrap(eventsRes);

          const mappedEvents: CalendarEvent[] =
            eventDataList?.map((e: any) => ({
              id: String(e.id),
              title: e.title,
              start: e.startTime,
              end: e.endTime,
              backgroundColor: e.backgroundColor,
              discordUrl: e.discordUrl,
              subjectId: e.subjectId ?? null,
              subjectTitle:
                subjects.find((s) => s.id === Number(e.subjectId))?.label ??
                undefined,
              classId: group.value,
              classTitle: group.label,
              subjects: subjects.map((s) => s.label),
            })) ?? [];

          return { groupId: group.value, subjects, events: mappedEvents };
        } catch (error) {
          console.log(error);
          return { groupId: group.value, subjects: [] as SubjectOption[], events: [] };
        }
      }),
    );

    const subjectMap: Record<number, SubjectOption[]> = {};
    results.forEach((result) => {
      subjectMap[result.groupId] = result.subjects;
    });

    setSubjectOptionsByGroup((prev) => ({ ...prev, ...subjectMap }));
    setEvents(results.flatMap((result) => result.events));
    setEventData((prev) => {
      const currentClassId = prev.classId ?? groupsToLoad[0]?.value ?? null;
      const optionsForClass =
        currentClassId != null
          ? subjectMap[currentClassId] ??
            subjectOptionsByGroup[currentClassId] ??
            []
          : [];
      const nextSubjectId =
        prev.subjectId !== null ? prev.subjectId : optionsForClass[0]?.id ?? null;
      return { ...prev, classId: currentClassId, subjectId: nextSubjectId };
    });
  };

  const displayedEvents = useMemo(() => {
    if (!selectedGroups.length) return events;
    const ids = selectedGroups.map((g) => g.value);
    return events.filter((evt) => ids.includes(evt.classId));
  }, [events, selectedGroups]);

  const selectedSubjects = useMemo(() => {
    const ids = selectedGroups.map((g) => g.value);
    const bag = new Set<string>();
    ids.forEach((id) => {
      (subjectOptionsByGroup[id] ?? []).forEach((s) => bag.add(s.label));
    });
    return Array.from(bag);
  }, [selectedGroups, subjectOptionsByGroup]);

  const handlePrevClick = () => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.prev();
      updateCurrentDate(api.getDate());
    }
  };

  const handleNextClick = () => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.next();
      updateCurrentDate(api.getDate());
    }
  };

  const handleTodayClick = () => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.today();
      updateCurrentDate(api.getDate());
    }
  };

  const updateCurrentDate = (date: Date) => {
    setCurrentDate(moment(date).format("MMMM, YYYY"));
  };

  const handleOpen = () => {
    const defaultClassId =
      selectedGroups[0]?.value ?? groupOptions[0]?.value ?? null;
    const defaultSubjectId =
      (defaultClassId ? subjectOptionsByGroup[defaultClassId]?.[0]?.id : null) ??
      null;

    setOpen(true);
    setIsEditMode(false);
    setEventData({
      id: null,
      title: "",
      start: "",
      end: "",
      backgroundColor: colorOptions[0].rgba,
      classId: defaultClassId,
      subjectId: defaultSubjectId,
      discordUrl: "",
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }> | React.ChangeEvent<{ name?: string; value: unknown }> | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name as string]: value,
    });
  };

  const handleSubmit = async () => {
    if (!eventData.title.trim() || !eventData.start || !eventData.end || !eventData.classId) {
      return;
    }

    const payload = {
      title: eventData.title,
      backgroundColor: eventData.backgroundColor,
      endTime: eventData.end,
      startTime: eventData.start,
      subjectId: eventData.subjectId,
      discordUrl: eventData.discordUrl,
    };

    try {
      if (isEditMode && eventData.id) {
        const res = await updateEventGroupService(
          eventData.classId,
          Number(eventData.id),
          payload,
        );
        const updated = res?.data;
        const classTitle =
          groupOptions.find((g) => g.value === eventData.classId)?.label ?? "";
        const subjectOptions = subjectOptionsByGroup[eventData.classId] ?? [];
        const subjectId =
          updated?.subjectId ?? eventData.subjectId ?? null;
        const subjectTitle =
          subjectOptions.find((s) => s.id === subjectId)?.label ?? undefined;
        const subjects = subjectOptions.map((s) => s.label);

        setEvents((prevEvents) =>
          prevEvents.map((evt) =>
            evt.id === eventData.id
              ? {
                  ...evt,
                  title: updated?.title ?? eventData.title,
                  start: updated?.startTime ?? eventData.start,
                  end: updated?.endTime ?? eventData.end,
                  backgroundColor: updated?.backgroundColor ?? eventData.backgroundColor,
                  discordUrl: updated?.discordUrl ?? eventData.discordUrl,
                  classId: eventData.classId!,
                  classTitle,
                  subjectId,
                  subjectTitle,
                  subjects,
                }
              : evt,
          ),
        );
      } else {
        const res = await addEventToGroupService(eventData.classId, payload);
        const created = res?.data;
        const classTitle =
          groupOptions.find((g) => g.value === eventData.classId)?.label ?? "";
        const subjectOptions = subjectOptionsByGroup[eventData.classId] ?? [];
        const subjectId =
          created?.subjectId ?? eventData.subjectId ?? null;
        const subjectTitle =
          subjectOptions.find((s) => s.id === subjectId)?.label ?? undefined;
        const subjects = subjectOptions.map((s) => s.label);

        setEvents((prevEvents) => [
          ...prevEvents,
          {
            id: String(created?.id ?? Date.now()),
            title: created?.title ?? eventData.title,
            start: created?.startTime ?? eventData.start,
            end: created?.endTime ?? eventData.end,
            backgroundColor: created?.backgroundColor ?? eventData.backgroundColor,
            discordUrl: created?.discordUrl ?? eventData.discordUrl,
            classId: eventData.classId!,
            classTitle,
            subjectId,
            subjectTitle,
            subjects,
          },
        ]);
      }
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  const removeEventById = (eventId: string) => {
    setEvents((prev) => prev.filter((evt) => evt.id !== eventId));
  };

  const handleDelete = async () => {
    if (!eventData.classId || !eventData.id) return;
    if (!window.confirm("Supprimer cet AcvAcnement ?")) return;
    try {
      await deleteEventGroupService(eventData.classId, Number(eventData.id));
      removeEventById(eventData.id);
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteFromDetails = async () => {
    if (!selectedEventDetails || !canManageEvents) return;
    if (!window.confirm("Supprimer cet AcvAcnement ?")) return;
    try {
      await deleteEventGroupService(
        selectedEventDetails.classId,
        Number(selectedEventDetails.id),
      );
      removeEventById(selectedEventDetails.id);
      setDetailsModalOpen(false);
      setSelectedEventDetails(null);
    } catch (e) {
      console.log(e);
    }
  };

  const handleEventClick = (info: { event: EventApi }) => {
    // Find the full event details
    const fullEvent = events.find(evt => evt.id === String(info.event.id));
    
    if (fullEvent) {
      setSelectedEventDetails(fullEvent);
      setDetailsModalOpen(true);
    }
  };

  const handleEditFromDetails = () => {
    if (selectedEventDetails && (userRole === "ROLE_ADMIN" || userRole === "ROLE_SUPER_TEACHER")) {
      const start = selectedEventDetails.start
        ? moment(selectedEventDetails.start).format("YYYY-MM-DDTHH:mm")
        : "";
      const end = selectedEventDetails.end
        ? moment(selectedEventDetails.end).format("YYYY-MM-DDTHH:mm")
        : "";

      setIsEditMode(true);
      setOpen(true);
      setDetailsModalOpen(false);
      setEventData({
        id: selectedEventDetails.id,
        title: selectedEventDetails.title,
        start,
        end,
        backgroundColor: selectedEventDetails.backgroundColor || colorOptions[0].rgba,
        classId: selectedEventDetails.classId,
        subjectId: selectedEventDetails.subjectId ?? null,
        discordUrl: selectedEventDetails.discordUrl || "",
      });
    }
  };

  const handleCustomButtonClick = (viewType: string) => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.changeView(viewType);
      setActiveView(viewType);
    }
  };

  const handleClassChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    const nextClassId = Number.isNaN(value) ? null : value;
    const nextSubjectId =
      nextClassId != null
        ? subjectOptionsByGroup[nextClassId]?.[0]?.id ?? null
        : null;
    setEventData((prev) => ({
      ...prev,
      classId: nextClassId,
      subjectId: nextSubjectId,
    }));
  };

  const handleSubjectChange = (event: SelectChangeEvent<number | string>) => {
    const rawValue = event.target.value;
    const numericValue =
      rawValue === "" || rawValue === null || rawValue === undefined
        ? null
        : Number(rawValue);
    setEventData((prev) => ({
      ...prev,
      subjectId: Number.isNaN(numericValue) ? null : numericValue,
    }));
  };

  const subjectOptionsForCurrentClass =
    eventData.classId != null
      ? subjectOptionsByGroup[eventData.classId] ?? []
      : [];

  const eventsForSelectedDate = useMemo(() => {
    if (!selectedDate) return displayedEvents;
    const dateStr = moment(selectedDate).format('YYYY-MM-DD');
    return displayedEvents.filter(evt => {
      const eventDate = moment(evt.start).format('YYYY-MM-DD');
      return eventDate === dateStr;
    });
  }, [selectedDate, displayedEvents]);

  const sortedEventsList = useMemo(() => {
    return [...displayedEvents].sort((a, b) => {
      return moment(a.start).valueOf() - moment(b.start).valueOf();
    });
  }, [displayedEvents]);

  return (
    <div className="w-full max-w-full space-y-4 overflow-hidden sm:space-y-6">
      <Header
        currentDate={currentDate}
        onPrev={handlePrevClick}
        onNext={handleNextClick}
        onToday={handleTodayClick}
        onWeekView={() => handleCustomButtonClick("timeGridWeek")}
        onMonthView={() => handleCustomButtonClick("dayGridMonth")}
        activeView={activeView}
        onOpenModal={handleOpen}
        groupOptions={groupOptions}
        selectedGroups={selectedGroups}
        onGroupsChange={setSelectedGroups}
        subjectTags={selectedSubjects}
        canAddEvents={canManageEvents}
        loading={loading}
        totalEvents={displayedEvents.length}
      />

      {/* Two Column Layout: Compact Calendar + Event List */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Compact Calendar - Left Side */}
        <div className="relative xl:col-span-7">
          <div className="absolute inset-0 bg-gradient-to-br from-[#09745F]/10 via-[#00B69B]/8 to-[#0A9C82]/10 rounded-[2rem] blur-3xl -z-10" />
          <div className="relative p-3 sm:p-4 lg:p-6 border-2 shadow-2xl bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-[2rem] border-white/60 transition-all duration-300 hover:shadow-[0_20px_60px_rgba(9,116,95,0.15)] h-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-[#09745F]">📅 Calendrier</h2>
              <button
                onClick={() => setCalendarModalOpen(true)}
                className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#09745F] to-[#00B69B] rounded-lg sm:rounded-xl hover:shadow-lg transition-colors transition-shadow duration-300"
              >
                Agrandir
              </button>
            </div>
            <div className="calendar-compact">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={displayedEvents}
                height={"auto"}
                contentHeight={"auto"}
                aspectRatio={1.2}
                eventClick={handleEventClick}
                dateClick={(info: DateClickArg) => setSelectedDate(info.date)}
                eventContent={renderCompactEventContent}
                headerToolbar={false}
                eventDisplay="block"
                dayMaxEvents={2}
                eventClassNames={() => "rounded-lg cursor-pointer"}
              />
            </div>
          </div>
        </div>

        {/* Event List - Right Side */}
        <div className="relative xl:col-span-5">
          <div className="absolute inset-0 bg-gradient-to-br from-[#09745F]/10 via-[#00B69B]/8 to-[#0A9C82]/10 rounded-[2rem] blur-3xl -z-10" />
          <div className="relative p-3 sm:p-4 lg:p-6 border-2 shadow-2xl bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-[2rem] border-white/60 transition-all duration-300 hover:shadow-[0_20px_60px_rgba(9,116,95,0.15)] h-full">
            <div className="flex flex-col items-start justify-between gap-2 mb-3 sm:flex-row sm:items-center sm:mb-4">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-[#09745F]">
                📋 Événements {selectedDate && <span className="hidden sm:inline">- {moment(selectedDate).format('D MMMM YYYY')}</span>}
              </h2>
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate(null)}
                  className="px-2 py-1 text-xs sm:text-sm font-semibold text-[#09745F] bg-[#09745F]/10 rounded-lg hover:bg-[#09745F]/20 transition-all duration-300 whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Tous les événements</span>
                  <span className="sm:hidden">Tous</span>
                </button>
              )}
            </div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {(selectedDate ? eventsForSelectedDate : sortedEventsList).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-[#09745F]/20 to-[#00B69B]/20 flex items-center justify-center">
                    <span className="text-4xl">📅</span>
                  </div>
                  <p className="text-lg font-semibold text-slate-600">Aucun événement</p>
                  <p className="mt-1 text-sm text-slate-400">
                    {selectedDate ? 'pour cette date' : 'à afficher'}
                  </p>
                </div>
              ) : (
                (selectedDate ? eventsForSelectedDate : sortedEventsList).map((event) => (
                  <EventListItem
                    key={event.id}
                    event={event}
                    onClick={() => {
                      setSelectedEventDetails(event);
                      setDetailsModalOpen(true);
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Calendar Modal */}
      <Dialog 
        open={calendarModalOpen} 
        onClose={() => setCalendarModalOpen(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 6,
            background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
            backdropFilter: "blur(24px)",
            boxShadow: "0 32px 64px -12px rgba(0, 0, 0, 0.3)",
            border: "2px solid rgba(255, 255, 255, 0.8)",
            minHeight: "80vh",
          }
        }}
      >
        <DialogTitle sx={{ fontSize: "1.75rem", fontWeight: 800, color: "#09745F", pb: 2, pt: 4, px: 4 }}>
          📅 Calendrier Complet
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 4, pb: 4 }}>
          <div className="relative">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={displayedEvents}
              height={"calc(80vh - 150px)"}
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,listWeek'
              }}
              eventDisplay="block"
              eventClassNames={() => "rounded-lg cursor-pointer"}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 6,
            background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
            backdropFilter: "blur(24px)",
            boxShadow: "0 32px 64px -12px rgba(0, 0, 0, 0.3)",
            border: "2px solid rgba(255, 255, 255, 0.8)",
          }
        }}
      >
        <DialogTitle sx={{ fontSize: "1.75rem", fontWeight: 800, color: "#09745F", pb: 2, pt: 4, px: 4 }}>
          {isEditMode ? "Mettre à jour l'événement" : "Ajouter un événement"}
        </DialogTitle>
        <DialogContent className="space-y-5" sx={{ pt: 3, px: 4 }}>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel sx={{ fontWeight: 600 }}>Groupe</InputLabel>
            <Select
              name="classId"
              value={eventData.classId ?? ""}
              onChange={handleClassChange}
              label="Groupe"
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: "rgba(9, 116, 95, 0.2)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(9, 116, 95, 0.4)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#09745F",
                }
              }}
            >
              {groupOptions.map((group) => (
                <MenuItem key={group.value} value={group.value}>
                  {group.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel sx={{ fontWeight: 600 }}>Matiere</InputLabel>
            <Select
              name="subjectId"
              value={eventData.subjectId ?? ""}
              onChange={handleSubjectChange}
              label="Matiere"
              displayEmpty
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: "rgba(9, 116, 95, 0.2)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(9, 116, 95, 0.4)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#09745F",
                }
              }}
            >
              <MenuItem value="">
                <em>Groupe complet</em>
              </MenuItem>
              {subjectOptionsForCurrentClass.length === 0 && (
                <MenuItem disabled value="__no-subjects">
                  <em>Aucune matiere pour ce groupe</em>
                </MenuItem>
              )}
              {subjectOptionsForCurrentClass.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Titre de l'evenement"
            type="text"
            fullWidth
            variant="outlined"
            name="title"
            value={eventData.title}
            onChange={handleChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "& fieldset": {
                  borderWidth: 2,
                  borderColor: "rgba(9, 116, 95, 0.2)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(9, 116, 95, 0.4)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#09745F",
                }
              },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
              }
            }}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              margin="dense"
              label="Debut"
              type="datetime-local"
              fullWidth
              variant="outlined"
              name="start"
              value={eventData.start}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "& fieldset": {
                    borderWidth: 2,
                    borderColor: "rgba(9, 116, 95, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(9, 116, 95, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#09745F",
                  }
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 600,
                }
              }}
            />
            <TextField
              margin="dense"
              label="Fin"
              type="datetime-local"
              fullWidth
              variant="outlined"
              name="end"
              value={eventData.end}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "& fieldset": {
                    borderWidth: 2,
                    borderColor: "rgba(9, 116, 95, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(9, 116, 95, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#09745F",
                  }
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 600,
                }
              }}
            />
          </div>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel sx={{ fontWeight: 600 }}>Couleur</InputLabel>
            <Select
              name="backgroundColor"
              value={eventData.backgroundColor}
              //@ts-ignore
              onChange={handleChange}
              label="Couleur"
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: "rgba(9, 116, 95, 0.2)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(9, 116, 95, 0.4)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#09745F",
                }
              }}
            >
              {colorOptions.map((color) => (
                <MenuItem key={color.hex} value={color.rgba}>
                  <div
                    style={{
                      backgroundColor: color.rgba,
                      width: "24px",
                      height: "24px",
                      display: "inline-block",
                      marginRight: "12px",
                      borderRadius: "8px",
                      border: "2px solid rgba(0,0,0,0.1)",
                    }}
                  />
                  <span style={{ fontWeight: 600 }}>{color.hex}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Lien Discord (optionnel)"
            type="url"
            fullWidth
            variant="outlined"
            name="discordUrl"
            value={eventData.discordUrl}
            onChange={handleChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "& fieldset": {
                  borderWidth: 2,
                  borderColor: "rgba(9, 116, 95, 0.2)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(9, 116, 95, 0.4)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#09745F",
                }
              },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 3, gap: 2 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined" 
            sx={{ 
              borderRadius: 3, 
              textTransform: "none", 
              fontWeight: 700,
              borderWidth: 2,
              borderColor: "#d1d5db",
              color: "#6b7280",
              px: 3,
              py: 1.5,
              "&:hover": { 
                borderWidth: 2,
                borderColor: "#9ca3af", 
                backgroundColor: "#f9fafb",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Annuler
          </Button>
          {isEditMode && (
            <Button 
              onClick={handleDelete} 
              variant="outlined" 
              sx={{ 
                borderRadius: 3, 
                textTransform: "none", 
                fontWeight: 700,
                borderWidth: 2,
                borderColor: "#fca5a5",
                color: "#dc2626",
                px: 3,
                py: 1.5,
                "&:hover": { 
                  borderWidth: 2,
                  borderColor: "#ef4444", 
                  backgroundColor: "#fef2f2",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(239,68,68,0.2)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Supprimer
            </Button>
          )}
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ 
              borderRadius: 3, 
              textTransform: "none", 
              fontWeight: 700,
              px: 4,
              py: 1.5,
              background: "linear-gradient(135deg, #00B69B 0%, #09745F 100%)",
              boxShadow: "0 6px 20px rgba(0, 182, 155, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #09745F 0%, #07594A 100%)",
                boxShadow: "0 8px 28px rgba(0, 182, 155, 0.5)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {isEditMode ? "Mettre à jour" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Modal */}
      <Dialog 
        open={detailsModalOpen} 
        onClose={() => setDetailsModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 6,
            background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
            backdropFilter: "blur(24px)",
            boxShadow: "0 32px 64px -12px rgba(0, 0, 0, 0.3)",
            border: "2px solid rgba(255, 255, 255, 0.8)",
          }
        }}
      >
        <DialogTitle sx={{ fontSize: "1.75rem", fontWeight: 800, color: "#09745F", pb: 2, pt: 4, px: 4 }}>
          📅 Détails de l'événement
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 4, pb: 2 }}>
          {selectedEventDetails && (
            <div className="space-y-4">
              {/* Event Title */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-[#09745F]/5 to-[#00B69B]/5 border-2 border-[#09745F]/20">
                <h3 className="text-2xl font-bold text-[#09745F] mb-1">{selectedEventDetails.title}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="px-3 py-1 font-semibold bg-white rounded-full shadow-sm">
                    {selectedEventDetails.classTitle}
                  </span>
                </div>
              </div>

              {/* Time Information */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="p-4 bg-white border-2 border-gray-100 shadow-sm rounded-xl">
                  <div className="mb-1 text-xs font-semibold text-gray-500 uppercase">Début</div>
                  <div className="text-lg font-bold text-[#09745F]">
                    {moment(selectedEventDetails.start).format("DD/MM/YYYY")}
                  </div>
                  <div className="text-base font-semibold text-gray-700">
                    {moment(selectedEventDetails.start).format("HH:mm")}
                  </div>
                </div>
                <div className="p-4 bg-white border-2 border-gray-100 shadow-sm rounded-xl">
                  <div className="mb-1 text-xs font-semibold text-gray-500 uppercase">Fin</div>
                  <div className="text-lg font-bold text-[#09745F]">
                    {moment(selectedEventDetails.end).format("DD/MM/YYYY")}
                  </div>
                  <div className="text-base font-semibold text-gray-700">
                    {moment(selectedEventDetails.end).format("HH:mm")}
                  </div>
                </div>
              </div>

              {/* Subject Information */}
              {selectedEventDetails.subjectTitle && (
                <div className="p-4 bg-white border-2 border-gray-100 shadow-sm rounded-xl">
                  <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">Matière</div>
                  <div className="text-lg font-bold text-[#09745F]">{selectedEventDetails.subjectTitle}</div>
                </div>
              )}

              {/* All Subjects */}
              {!selectedEventDetails.subjectTitle && selectedEventDetails.subjects && selectedEventDetails.subjects.length > 0 && (
                <div className="p-4 bg-white border-2 border-gray-100 shadow-sm rounded-xl">
                  <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">Matières disponibles</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedEventDetails.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="px-3 py-1.5 text-sm font-semibold rounded-full bg-gradient-to-br from-[#09745F]/10 to-[#00B69B]/10 text-[#09745F] border border-[#09745F]/20"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Discord Link */}
              {selectedEventDetails.discordUrl && (
                              <div className="p-4 border-2 border-[#10b981]/30 shadow-sm rounded-xl bg-gradient-to-br from-[#10b981]/10 to-[#059669]/10">
                                <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">Lien de réunion</div>
                                <a
                                  href={selectedEventDetails.discordUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-[#10b981] to-[#059669] rounded-xl hover:from-[#059669] hover:to-[#10b981] hover:shadow-xl"
                                >
                                  🔗 Rejoindre sur Discord
                                </a>
                              </div>
              )}

              {/* Color Indicator */}
              {/* <div className="p-4 bg-white border-2 border-gray-100 shadow-sm rounded-xl">
                <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">Couleur</div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 border-2 border-white shadow-md rounded-xl"
                    style={{ backgroundColor: selectedEventDetails.backgroundColor }}
                  />
                  <span className="text-sm font-medium text-gray-600">
                    {selectedEventDetails.backgroundColor}
                  </span>
                </div>
              </div> */}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 2, gap: 2 }}>
          <button
            onClick={() => setDetailsModalOpen(false)}
            className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            Fermer
          </button>
          {canManageEvents && (
            <>
              <button
                onClick={handleDeleteFromDetails}
                className="px-6 py-2.5 text-sm font-semibold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-all duration-200 shadow-sm hover:shadow-md border border-red-100"
              >
                Supprimer
              </button>
              <button
                onClick={handleEditFromDetails}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#09745F] to-[#00B69B] rounded-xl hover:from-[#00B69B] hover:to-[#09745F] transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Modifier
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

function renderEventContent(eventInfo: EventContentArg) {
  const startTime = moment(eventInfo.event.start).format("HH:mm");
  const endTime = moment(eventInfo.event.end).format("HH:mm");
  const title = eventInfo.event.title;
  const subjectTitle = eventInfo.event.extendedProps.subjectTitle as string | undefined;
  const subjects = (eventInfo.event.extendedProps.subjects as string[]) ?? [];

  return (
    <div
      className="flex flex-col justify-between p-2.5 cursor-pointer"
      style={{
        background: `${eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.7)') || "rgba(241,245,249,0.85)"}`,
        borderRadius: 8,
        minHeight: 70,
        border: "1px solid rgba(255, 255, 255, 0.5)",
      }}
    >
      {/* Event Title */}
      <div className="mb-1.5">
        <h3 className="text-xs font-bold text-gray-900 sm:text-sm line-clamp-2">
          {title}
        </h3>
      </div>
      
      {/* Time badge */}
      <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold w-fit rounded bg-white/80 text-slate-700">
        <svg className="w-3 h-3 text-[#10b981]" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        <span>{startTime}</span>
        <span>-</span>
        <span>{endTime}</span>
      </div>
      
      {/* Subject badge - single subject */}
      {subjectTitle && (
        <div className="mt-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-white/80 text-slate-700 rounded truncate max-w-full">
            <svg className="flex-shrink-0 w-3 h-3 text-[#09745F]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            <span className="truncate">{subjectTitle}</span>
          </span>
        </div>
      )}
      
      {/* Multiple subjects */}
      {!subjectTitle && subjects.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-white/80 text-slate-700 rounded truncate max-w-full">
            <svg className="flex-shrink-0 w-3 h-3 text-[#09745F]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            <span className="truncate">{subjects[0]}</span>
          </span>
          {subjects.length > 1 && (
            <span className="inline-flex items-center justify-center px-1.5 py-1 text-[10px] font-semibold bg-white/70 text-slate-600 rounded">
              +{subjects.length - 1}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

type HeaderProps = {
  currentDate: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onWeekView: () => void;
  onMonthView: () => void;
  activeView: string;
  onOpenModal: () => void;
  groupOptions: GroupOption[];
  selectedGroups: GroupOption[];
  onGroupsChange: (value: GroupOption[]) => void;
  subjectTags: string[];
  canAddEvents: boolean;
  loading: boolean;
  totalEvents: number;
};

const Header: React.FC<HeaderProps> = ({
  currentDate,
  onPrev,
  onNext,
  onToday,
  onWeekView,
  onMonthView,
  activeView,
  onOpenModal,
  groupOptions,
  selectedGroups,
  onGroupsChange,
  subjectTags,
  canAddEvents,
  loading,
  totalEvents,
}) => {
  return (
    <div className="relative overflow-hidden text-white shadow-2xl rounded-xl sm:rounded-2xl lg:rounded-[2rem] bg-gradient-to-br from-[#09745F] via-[#00B69B] to-[#0A9C82] max-w-full">
      {/* Static gradient overlays */}
      <div className="absolute inset-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.3),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.25),transparent_40%)]" />
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.6'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
      
      <div className="relative z-10 max-w-full p-3 space-y-2 overflow-hidden sm:p-5 sm:space-y-4 lg:p-8">
        {/* Header title and navigation */}
        <div className="flex flex-col gap-2 sm:gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 min-w-0 space-y-1">
            <Typography variant="h4" className="!text-white !font-bold !text-base sm:!text-xl lg:!text-3xl !leading-tight !drop-shadow-lg truncate">
              📅 Agenda des matières
            </Typography>
            <Typography variant="body1" className="!text-white/90 !text-xs sm:!text-sm lg:!text-base !leading-relaxed !drop-shadow-md max-w-2xl hidden sm:block">
              Visualisez et gérez tous vos créneaux de cours et événements en un seul endroit.
            </Typography>
          </div>
          
          {/* Navigation controls - responsive */}
          <div className="flex items-center flex-shrink-0 gap-1 sm:gap-2">
            <IconButton
              className="!text-white !bg-white/20 hover:!bg-white/30 active:!bg-white/40 !backdrop-blur-md !shadow-lg !transition-colors !transition-shadow !duration-300 !border !border-white/40 !rounded-lg sm:!rounded-xl !w-8 !h-8 sm:!w-10 sm:!h-10 !min-w-0"
              onClick={onPrev}
              size="small"
            >
              <ArrowLeft sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
            </IconButton>
            <Button
              onClick={onToday}
              variant="outlined"
              color="inherit"
              startIcon={<Today sx={{ fontSize: { xs: '14px', sm: '18px' }, display: { xs: 'none', sm: 'inline' } }} />}
              className="!border-white/50 !text-white hover:!border-white hover:!bg-white/20 !backdrop-blur-md !shadow-lg !transition-colors !transition-shadow !duration-300 !px-2 sm:!px-4 lg:!px-6 !py-1 sm:!py-2 !font-semibold !rounded-lg sm:!rounded-xl !text-[10px] sm:!text-xs lg:!text-sm whitespace-nowrap !min-w-0"
              size="small"
            >
              Aujourd&apos;hui
            </Button>
            <IconButton
              className="!text-white !bg-white/20 hover:!bg-white/30 active:!bg-white/40 !backdrop-blur-md !shadow-lg !transition-colors !transition-shadow !duration-300 !border !border-white/40 !rounded-lg sm:!rounded-xl !w-8 !h-8 sm:!w-10 sm:!h-10 !min-w-0"
              onClick={onNext}
              size="small"
            >
              <ArrowRight sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
            </IconButton>
          </div>
        </div>

        {/* Subject tags with animation */}
        {/* {subjectTags.length > 0 && (
          <div className="flex flex-wrap gap-2 animate-fadeInDown">
            {subjectTags.map((subject, idx) => (
              <span
                key={subject}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full bg-white/25 backdrop-blur-md text-white border border-white/50 shadow-lg hover:bg-white/35 hover:scale-105 transition-all duration-300 cursor-default"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                {subject}
              </span>
            ))}
          </div>
        )} */}

          {/* Loading indicator */}
        {/* {loading && (
          <div className="flex items-center gap-2 pt-1 text-sm font-semibold text-white drop-shadow-md">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            <span>Chargement des événements...</span>
          </div>
        )} */}

        {/* View switcher and stats - improved responsive layout */}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          {/* <div className="flex items-center gap-2 p-1 sm:p-1.5 bg-white/15 backdrop-blur-md rounded-2xl border border-white/40 shadow-inner w-fit">
            <Button
              onClick={onWeekView}
              variant={activeView === "timeGridWeek" ? "contained" : "text"}
              color="inherit"
              startIcon={<CalendarMonth fontSize="small" />}
              className={
                activeView === "timeGridWeek"
                  ? "!bg-white !text-[#09745F] !shadow-xl !font-bold !rounded-xl !px-4 sm:!px-5 !py-2 !transition-all !duration-300 !transform hover:!scale-105"
                  : "!text-white hover:!bg-white/20 !transition-all !duration-300 !rounded-xl !px-4 sm:!px-5 !py-2 hover:!scale-105"
              }
              size="medium"
            >
              <span className="hidden sm:inline">Semaine</span>
              <span className="sm:hidden">Sem.</span>
            </Button>
            <Button
              onClick={onMonthView}
              variant={activeView === "dayGridMonth" ? "contained" : "text"}
              color="inherit"
              startIcon={<CalendarMonth fontSize="small" />}
              className={
                activeView === "dayGridMonth"
                  ? "!bg-white !text-[#09745F] !shadow-xl !font-bold !rounded-xl !px-4 sm:!px-5 !py-2 !transition-all !duration-300 !transform hover:!scale-105"
                  : "!text-white hover:!bg-white/20 !transition-all !duration-300 !rounded-xl !px-4 sm:!px-5 !py-2 hover:!scale-105"
              }
              size="medium"
            >
              Mois
            </Button>
          </div> */}

          {/* Stats and action button */}
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
            {/* Stats card */}
            <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 border shadow-xl rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-lg border-white/50 hover:bg-white/25 transition-colors transition-shadow duration-300 flex-1 sm:flex-none sm:min-w-[180px]">
              <div className="text-[9px] sm:text-[10px] lg:text-xs font-bold tracking-widest text-white/90 uppercase">
                {currentDate}
              </div>
              {/* <div className="flex items-baseline gap-2">
                <div className="text-2xl font-black text-white sm:text-3xl">{totalEvents}</div>
                <div className="text-sm font-semibold sm:text-base text-white/90">événements</div>
              </div> */}
            </div>
            
            {/* Add event button */}
            {canAddEvents && (
              <Button
                onClick={onOpenModal}
                variant="contained"
                startIcon={<Add sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                className="!bg-white !text-[#09745F] hover:!bg-white/95 !shadow-2xl !transition-colors !transition-shadow !duration-300 !font-bold !px-3 sm:!px-5 lg:!px-7 !py-2 sm:!py-3 lg:!py-3.5 !rounded-xl sm:!rounded-2xl !text-xs sm:!text-sm lg:!text-base whitespace-nowrap"
                size="small"
              >
                <span className="hidden sm:inline">Ajouter un événement</span>
                <span className="sm:hidden">+ Événement</span>
              </Button>
            )}
          </div>
        </div>

        {/* Group selector with modern styling */}
        <div className="flex flex-col w-full max-w-full gap-3 overflow-hidden sm:gap-4">
          <div className="inline-flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-widest text-white uppercase transition-all duration-300 border-2 border-white/40 rounded-xl sm:px-5 sm:py-2.5 sm:text-sm backdrop-blur-md hover:border-white/60" 
                 style={{ 
                   background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
                   boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                 }}>
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Groupes visibles</span>
            </div>
          </div>
          <Autocomplete
            multiple
            options={groupOptions}
            getOptionLabel={(option) => option.label}
            value={selectedGroups}
            onChange={(_, value) => onGroupsChange(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  deleteIcon={
                    <span style={{ 
                      fontSize: 'inherit', 
                      display: 'flex', 
                      alignItems: 'center',
                      opacity: 0.8,
                      transition: 'opacity 0.2s'
                    }}>×</span>
                  }
                  sx={{
                    backgroundColor: '#fff !important',
                    color: '#09745F !important',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', sm: '0.75rem', lg: '0.8125rem' },
                    height: { xs: '22px', sm: '26px', lg: '28px' },
                    borderRadius: { xs: '11px', sm: '13px', lg: '14px' },
                    border: '1.5px solid rgba(9, 116, 95, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiChip-label': {
                      padding: { xs: '0 6px', sm: '0 8px', lg: '0 10px' },
                      fontWeight: 600,
                      letterSpacing: '0.01em'
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: { xs: '16px', sm: '18px' },
                      marginRight: { xs: '2px', sm: '3px' },
                      marginLeft: { xs: '-2px', sm: '-1px' },
                      color: '#09745F',
                      opacity: 0.6,
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 1,
                        color: '#064d3f',
                      }
                    },
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                      transform: 'translateY(-1px)',
                      borderColor: 'rgba(9, 116, 95, 0.25)',
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                // label="Groupes visibles"
                placeholder={selectedGroups.length === 0 ? "Sélectionner des groupes..." : ""}
                InputProps={{
                  ...params.InputProps,
                  className: "!text-white",
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                  sx: {
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', lg: '0.875rem' },
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                  }
                }}
                size="small"
              />
            )}
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              "& .MuiInputBase-root": {
                color: "white",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderRadius: { xs: '12px', sm: '14px', lg: '16px' },
                border: '2px solid rgba(255, 255, 255, 0.25)',
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                fontWeight: 500,
                padding: { xs: "4px 6px", sm: "6px 10px", lg: "8px 12px" },
                fontSize: { xs: '0.7rem', sm: '0.8125rem', lg: '0.875rem' },
                minHeight: { xs: '44px', sm: '48px', lg: '52px' },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                flexWrap: "wrap",
                gap: { xs: '4px', sm: '6px' },
                maxWidth: '100%',
                overflow: 'hidden',
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.2)",
                  borderColor: "rgba(255, 255, 255, 0.35)",
                  boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-focused": {
                  background: "rgba(255, 255, 255, 0.22)",
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 8px 28px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 0 0 3px rgba(255, 255, 255, 0.1)",
                  transform: "translateY(-2px)",
                }
              },
              "& .MuiInputLabel-root": {
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.75rem', lg: '0.8rem' },
                letterSpacing: '0.08em',
                background: '#0fd850',
                padding: { xs: '2px 10px', sm: '3px 14px' },
                borderRadius: '999px',
                border: '1.5px solid #fff',
                boxShadow: 'none',
                zIndex: 2,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                transform: { xs: 'translate(0, -32px) scale(1)', sm: 'translate(0, -36px) scale(1)' },
                position: 'relative',
                width: 'fit-content',
                marginBottom: { xs: '4px', sm: '6px' },
                "&.Mui-focused": {
                  color: "#fff",
                  background: '#0fd850',
                  borderColor: '#fff',
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: 'none',
              },
              "& .MuiAutocomplete-endAdornment": {
                top: { xs: 'calc(50% - 12px)', sm: 'calc(50% - 14px)' },
                right: { xs: '6px !important', sm: '10px !important' },
                '& .MuiIconButton-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  padding: { xs: '2px', sm: '4px' },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: 'rgba(255, 255, 255, 1)',
                  }
                }
              },
              "& .MuiAutocomplete-tag": {
                maxWidth: { xs: 'calc(50% - 6px)', sm: 'calc(100% - 10px)' },
                margin: { xs: '1px !important', sm: '2px !important' }
              },
              "& .MuiAutocomplete-clearIndicator": {
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '16px', sm: '18px' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 1)',
                }
              },
              "& .MuiAutocomplete-popupIndicator": {
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 1)',
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Calendar;
